'use client';

/**
 * doc 64 §8.1 / D9 — how a guest reaches the assistant.
 *
 * ONE PILL, TWO HIT TARGETS: the left half opens chat, the right half opens
 * voice. Not two separate buttons, because `PlaceHeader`'s trailing slot
 * deliberately holds Search and You "and nothing else", and not one button with
 * a menu, because choosing between typing and talking should not itself cost a
 * tap.
 *
 * This REPLACES the floating bubble that used to sit bottom-right. That corner
 * belongs to the guest's order (PlaceShell's rule), and a circle hovering over
 * the last row's add-to-order button is in the way of the thing people actually
 * came to do. It also read as a bolted-on chat product rather than part of the
 * place.
 *
 * The mic half is not rendered where the browser cannot listen — offering a
 * control that silently does nothing is worse than not offering it. Opening
 * voice from a real tap also matters on iOS, where the first `speak()` must
 * happen inside a user gesture.
 */

import * as React from 'react';
import { MessageCircle, Mic } from 'lucide-react';

import { customerChatEnabled } from '@/lib/config-system/customer-chat';
import { cn } from '@/components/ui/utils';

export interface AiEntryPillProps {
  onOpenChat: () => void;
  /** Omit to render chat only — e.g. where speech recognition is unavailable. */
  onOpenVoice?: () => void;
  /** Draws attention once, when the assistant has something unread. */
  hasUnread?: boolean;
  assistantName?: string;
  className?: string;
}

export function AiEntryPill({
  onOpenChat,
  onOpenVoice,
  hasUnread = false,
  assistantName = 'the menu assistant',
  className,
}: AiEntryPillProps) {
  // Guest text-chat switch (configs/ai/customer-chat.json). This component has
  // no hooks, so an early return is safe — and it is the single choke point
  // for the pill on both host pages (the menu page and Chow), so neither host
  // had to be edited. The CustomerAssistant MOUNTS are gated separately on the
  // same switch, because that component has hooks and cannot early-return.
  if (!customerChatEnabled()) return null;

  return (
    <div
      className={cn(
        'inline-flex shrink-0 items-center rounded-full',
        'border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)]',
        className,
      )}
    >
      <button
        type="button"
        onClick={onOpenChat}
        aria-label={`Ask ${assistantName}`}
        className={cn(
          // 44px tall pill, per §11.5 — and 44 WIDE too. Below `sm` the word
          // "Ask" is hidden and only an 18px icon remains, which left this at
          // 38px on a phone: the one size that a class list reading `h-11`
          // hides, and the only viewport the guest surface is actually used on.
          'relative inline-flex h-11 min-w-11 items-center justify-center gap-1.5 rounded-full py-2 pl-3',
          onOpenVoice ? 'pr-2' : 'pr-3',
          'text-[13px] font-medium text-[var(--color-text-primary)]',
          'transition-colors hover:bg-[var(--color-bg-surface-hover)]',
          'outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-accent)]',
        )}
      >
        <MessageCircle aria-hidden className="size-[18px]" />
        <span className="hidden sm:inline">Ask</span>
        {hasUnread ? (
          <>
            <span
              aria-hidden
              className="absolute right-1 top-1.5 size-2 rounded-full bg-[var(--color-accent)]"
            />
            {/* The dot is decorative; the state itself has to be readable. */}
            <span className="sr-only">You have a new reply</span>
          </>
        ) : null}
      </button>

      {onOpenVoice ? (
        <>
          <span aria-hidden className="h-5 w-px bg-[var(--color-border-subtle)]" />
          <button
            type="button"
            onClick={onOpenVoice}
            aria-label={`Talk to ${assistantName}`}
            className={cn(
              // 44 in BOTH directions. It was 38 wide — tall enough to pass a
              // glance at the class list and still under the floor in practice.
              'inline-flex size-11 items-center justify-center rounded-full',
              'text-[var(--color-text-primary)] transition-colors',
              'hover:bg-[var(--color-bg-surface-hover)]',
              'outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-accent)]',
            )}
          >
            <Mic aria-hidden className="size-[18px]" />
          </button>
        </>
      ) : null}
    </div>
  );
}
